<script>
	import { createEventDispatcher } from "svelte";
	import { uiText } from "../lib/uiText.js";
	import Mapbox from "../play/Mapbox.svelte";
	import {
		doc,
		setDoc,
		getDoc,
		deleteField,
		GeoPoint 
	} from "firebase/firestore";

	const dispatch = createEventDispatcher();
	
	export let db;
	export let lang;
	export let loader;
	
	let ui = uiText(lang);

	$: if (lang) {
		ui = uiText(lang);
		dispatch("setLang", { lang: lang, prefset: true });
	}
	$: dispatch("loader", loader);

	let setup = "base";

	let openSet = {
		gid: "",
		rid: "",
	};

	let gameSet = {
		gid: "",
		max_team_size: 5,
		name: "",
		period: {
			from: new Date().toISOString().split("T")[0],
			to: new Date().toISOString().split("T")[0],
		},
		start: {
			link: "",
			title: "",
		},
		purchaseLink: "",

	};

	let riddleSet = {
		gid: "",
		rid: "",
		header: "default",
		main: "",
		aside: "default",
		footer: "default",
		customStyling: "",
		rd_content: {
			address: "",
			answers: [],
			hints: [],
			isBonus: false,
			type: "multiple",
			// if reorder
			order_id_list: [],
			order_list: [],
			order_size: {
				rows: 2,
				cols: 2,
			},
			order_type: "disabled",
			// if panel
			panel_id_list: [],
			panel_list: [],
			panel_thumbnail_list: [],
			panel_size: {
				rows: 2,
				cols: 2,
			},
			panel_type: "disabled",
		},
        		// --- GAME TIMER SETTINGS ---
		showTimer: false,
		timerStyling: ""
	};

	// --- MAPBOX STATE VARIABLES ---
	let hasMap = false;
	let map_content = {
		center: [24.73732, 42.15029], 
		zoom: 15,
		bounds: {
			sw: [23.98116, 42.11312],
			ne: [24.75014, 42.70434],
		},
		geolocate: true,
		navigation: true,
		style: "mapbox://styles/emiltoteb/cm7i3e3uf00ia01s2fl6c0a4f",
		markerStyling: "",
		markers: [],
		polygons: []
	};
	let mapPreviewKey = 0; 
	let polyCoordsText = [];
	// ----------------------------------

	// --- GEO-LOCKED STATE VARIABLES ---
	let hasGeoContent = false;
	let geo_content = {
		coords: [42.14579, 24.74753], 
		geolocked: true,
		range: 15,
		showGeoDetails: false,
		geo_main: "",
		options: {
			enableHighAccuracy: true,
			maximumAge: 0,
			timeout: 6000
		}
	};
	// ----------------------------------

	let complexAnswerTemp = [];

	const hint = {
		content: "",
		guide: "",
		time: 1,
	};

	let types = {
		names: [
			"Single answer",
			"Single answer with alternatives",
			"List multiple",
			"List multiple with alternatives",
			"Drag&Drop Reorder",
			"Drag&Drop Reorder, but show input",
			"Button order (thumbnails)",
			"Button order (hidden)",
			"Button to input",
			"QR Code (Single)",
			"QR Code (Any valid)",
			"QR Code (Multiple required)",
			"QR Code (Multiple valid, multiple needed)",
			"QR Code (Unlock)"
		],
		values: [
			"single",
			"multiple",
			"mc",
			"mc-multiple",
			"dnd-reorder",
			"dnd-reorder-show-input",
			"btn-order",
			"btn-order-hidden",
			"btn-to-input",
			"qr",
			"qr-mc",
			"qr-multiple",
			"qr-mc-multiple",
			"qr-unlock"
		],
	};

	function refreshMapPreview() {
		if (map_content.polygons) {
			map_content.polygons.forEach((poly, i) => {
				if (polyCoordsText[i]) {
					let lines = polyCoordsText[i].split("\n").filter(l => l.trim() !== "");
					let ring = lines.map(line => {
						let parts = line.split(",").map(n => parseFloat(n.trim()));
						return [parts[0] || 0, parts[1] || 0];
					});
					
					if (ring.length > 0) {
						let first = ring[0];
						let last = ring[ring.length - 1];
						if (first[0] !== last[0] || first[1] !== last[1]) {
							ring.push([...first]);
						}
						poly.coordinates = [ring];
					} else {
						poly.coordinates = [];
					}
				} else {
					poly.coordinates = [];
				}
			});
		}
		mapPreviewKey++;
	}

	async function saveGameSet() {
		let data = gameSet;

		let gameSetObject = {
			gid: data.gid,
			max_team_size: data.max_team_size,
			name: data.name,
			period: {
				from: new Date(data.period.from),
				to: new Date(data.period.to),
			},
			start: data.start,
			purchaseLink: data.purchaseLink,
			showTimer: data.showTimer, // Save boolean
			timerStyling: data.timerStyling // Save styling
		};

		const gameSetRef = doc(db, "games", data.gid);
		const checkSnap = await getDoc(gameSetRef);

		const message = checkSnap.exists()
			? "Are you sure you want to update the game?"
			: "A game with this GID doesn't exist. Do you want to create it?";

		if (confirm(message)) {
			await setDoc(gameSetRef, gameSetObject, { merge: true });
			alert("Game saved successfully!");
		} else {
			alert("No changes were made.");
		}
	}

	async function saveRiddleSet() {
		if (hasMap) refreshMapPreview();

		let data = riddleSet;

		let riddleSetObject = {
			header: data.header,
			main: data.main,
			aside: data.aside,
			footer: data.footer,
			customStyling: data.customStyling,
			map_content: hasMap ? map_content : deleteField(), 
			geo_content: hasGeoContent ? {
				geolocked: geo_content.geolocked,
				range: geo_content.range,
				showGeoDetails: geo_content.showGeoDetails,
				geo_main: geo_content.geo_main,
				options: geo_content.options,
				coords: new GeoPoint(geo_content.coords[0], geo_content.coords[1])
			} : deleteField(),
			rd_content: {
				address: data.rd_content.address,
				answers: data.rd_content.answers,
				hints: data.rd_content.hints,
				isBonus: data.rd_content.isBonus,
				type: data.rd_content.type,

				...((riddleSet.rd_content.type == "dnd-reorder" ||
					riddleSet.rd_content.type == "dnd-reorder-show-input") && {
					order_id_list: data.rd_content.order_id_list,
					order_list: data.rd_content.order_list,
					order_size: data.rd_content.order_size,
					order_type: data.rd_content.order_type,
				}),

				...((riddleSet.rd_content.type == "btn-order" ||
					riddleSet.rd_content.type == "btn-order-hidden" ||
					riddleSet.rd_content.type == "btn-to-input") && {
					panel_id_list: data.rd_content.panel_id_list,
					panel_list: data.rd_content.panel_list,
					panel_thumbnail_list: data.rd_content.panel_thumbnail_list,
					panel_size: data.rd_content.panel_size,
					panel_type: data.rd_content.panel_type,
				}),
			},
		};

		const riddleSetRef = doc(db, "games", data.gid, "gamedata", data.rid);
		const checkSnap = await getDoc(riddleSetRef);

		const message = checkSnap.exists()
			? "Are you sure you want to update the riddle?"
			: "A riddle with this RID doesn't exist. Do you want to create it?";

		if (confirm(message)) {
			await setDoc(riddleSetRef, riddleSetObject, { merge: true });
			alert("Riddle saved successfully!");
		} else {
			alert("No changes were made.");
		}
	}

	async function handleOpenGame() {
		const openGameRef = doc(db, "games", openSet.gid);
		const checkSnap = await getDoc(openGameRef);

		if (checkSnap.exists()) {
			let data = checkSnap.data();
			gameSet = {
				gid: data.gid,
				max_team_size: data.max_team_size,
				name: data.name,
				period: {
					from: data.period.from.toDate().toISOString().split("T")[0],
					to: data.period.to.toDate().toISOString().split("T")[0],
				},
				start: {
					link: data.start.link,
					title: data.start.title,
				},
				purchaseLink: data.purchaseLink,
				showTimer: data.showTimer || false, // Fetch timer bool
				timerStyling: data.timerStyling || "" // Fetch timer styling
			};
			setup = "game";
		} else {
			console.log("A game with this GID doesn't exist!");
		}
	}

	async function handleOpenRiddle() {
		const openRiddleRef = doc(
			db,
			"games",
			openSet.gid,
			"gamedata",
			openSet.rid,
		);
		const checkSnap = await getDoc(openRiddleRef);

		if (checkSnap.exists()) {
			let data = checkSnap.data();
			let tempAnswers = [];

			data.rd_content.answers.forEach((element) => {
				if (typeof element === "object") {
					tempAnswers.push(JSON.stringify(element));
				} else {
					tempAnswers.push(element);
				}
			});

			riddleSet = {
				gid: openSet.gid,
				rid: openSet.rid,
				header: data.header,
				main: data.main,
				aside: data.aside,
				footer: data.footer,
				customStyling: data.customStyling || "",
				rd_content: {
					address: data.rd_content.address,
					answers: tempAnswers,
					hints: data.rd_content.hints || [],
					isBonus: data.rd_content.isBonus || false,
					type: data.rd_content.type,
					...((data.rd_content.type == "dnd-reorder" ||
						data.rd_content.type == "dnd-reorder-show-input") && {
						order_id_list: data.rd_content.order_id_list,
						order_list: data.rd_content.order_list,
						order_size: data.rd_content.order_size,
						order_type: data.rd_content.order_type,
					}),
					...((data.rd_content.type == "btn-order" ||
						data.rd_content.type == "btn-order-hidden" ||
						data.rd_content.type == "btn-to-input") && {
						panel_id_list: data.rd_content.panel_id_list,
						panel_list: data.rd_content.panel_list,
						panel_thumbnail_list: data.rd_content.panel_thumbnail_list,
						panel_size: data.rd_content.panel_size,
						panel_type: data.rd_content.panel_type,
					}),
				},
			};

			if (data.map_content) {
				hasMap = true;
				map_content = data.map_content;
				
				if (!map_content.markers) map_content.markers = [];
				if (!map_content.polygons) map_content.polygons = [];
				
				polyCoordsText = map_content.polygons.map(poly => {
					if (!poly.coordinates || !poly.coordinates[0]) return "";
					return poly.coordinates[0].map(coord => coord.join(", ")).join("\n");
				});

				mapPreviewKey++; 
			} else {
				hasMap = false;
				map_content.markers = [];
				map_content.polygons = [];
				polyCoordsText = [];
			}

			if (data.geo_content) {
				hasGeoContent = true;
				geo_content = {
					geolocked: data.geo_content.geolocked ?? true,
					range: data.geo_content.range ?? 15,
					showGeoDetails: data.geo_content.showGeoDetails ?? false,
					geo_main: data.geo_content.geo_main ?? "",
					options: data.geo_content.options ?? { enableHighAccuracy: true, maximumAge: 0, timeout: 6000 },
					coords: data.geo_content.coords ? [data.geo_content.coords.latitude, data.geo_content.coords.longitude] : [42.14579, 24.74753]
				};
			} else {
				hasGeoContent = false;
				geo_content = {
					coords: [42.14579, 24.74753],
					geolocked: true,
					range: 15,
					showGeoDetails: false,
					geo_main: "",
					options: {
						enableHighAccuracy: true,
						maximumAge: 0,
						timeout: 6000
					}
				};
			}

			complexAnswerTemp = tempAnswers;
			setup = "riddle";
		} else {
			console.log("A riddle with this RID doesn't exist!");
		}
	}
</script>

<p class="mt-6 uppercase">VG Panel</p>
<h1>Game Creator</h1>

{#if setup == "base"}
	<button on:click={() => (setup = "game")}
		><i class="fa-solid fa-gamepad" data-position="left" />Setup Game</button>
	<button on:click={() => (setup = "riddle")}
		><i class="fa-solid fa-puzzle-piece" data-position="left" />Setup Riddle</button>
	<button on:click={() => (setup = "open-game")} class="secondary">
		<i class="fa-solid fa-folder-open" data-position="left" />Open Game</button>
	<button on:click={() => (setup = "open-riddle")} class="secondary"
		><i class="fa-solid fa-puzzle-piece" data-position="left" />Open Riddle</button>
{:else if setup == "game"}
	<div class="flex flex-wrap">
		<div class="grid grow sm:grow-0">
			<label for="gid">GID:</label>
			<input
				type="text"
				name="gid"
				maxlength="10"
				id="gid"
				bind:value={gameSet.gid}
				placeholder="Game ID"
				title="Game ID"
			/>
		</div>

		<div class="grid grow min-w-[50vw]">
			<label for="game_name">Game name:</label>
			<input
				type="text"
				name="game_name"
				id="game_name"
				bind:value={gameSet.name}
				placeholder="Game name"
				title="Game name"
			/>
		</div>
	</div>

	<div class="flex flex-wrap">
		<div class="grid grow">
			<label for="period_from">Period from:</label>
			<input
				type="date"
				name="period_from"
				id="period_from"
				bind:value={gameSet.period.from}
			/>
		</div>

		<div class="grid grow">
			<label for="period_to">Period to:</label>
			<input
				type="date"
				name="period_to"
				id="period_to"
				bind:value={gameSet.period.to}
			/>
		</div>
	</div>

	<div class="flex flex-wrap">
		<div class="grid grow">
			<label for="max_team_size">Max Team Size:</label>
			<input
				type="number"
				name="max_team_size"
				min="1"
				id="max_team_size"
				bind:value={gameSet.max_team_size}
			/>
		</div>

		<div class="grid grow min-w-[50vw]">
			<label for="purchase_link">Purchase link:</label>
			<input
				type="url"
				name="purchase_link"
				id="purchase_link"
				bind:value={gameSet.purchaseLink}
			/>
		</div>
	</div>

	<div class="flex flex-wrap">
		<div class="grid content-start grow sm:grow-0">
			<label for="start_title">Start title:</label>
			<input
				type="text"
				name="start_title"
				id="start_title"
				bind:value={gameSet.start.title}
			/>
		</div>

		<div class="grid grow min-w-[50vw]">
			<label for="start_link">Start link:</label>
			<input
				type="url"
				name="start_link"
				id="start_link"
				bind:value={gameSet.start.link}
			/>
			<p class="place-self-end">
				<small
					><a
						href="https://wildblue.digital/resources/map-link-generator/"
						target="_blank">Universal Map Link Generator</a
					></small
				>
			</p>
		</div>
	</div>

	
	<div class="grid flex-wrap sm:flex">
		<button on:click={() => (setup = "base")} class="outline grow">
			<i class="fa-solid fa-circle-left" data-position="left" />Back</button>
		<button
			on:click={saveGameSet}
			class="order-first secondary grow sm:order-none"
		>
			<i class="fa-solid fa-floppy-disk" data-position="left" />Save Game Set</button>
	</div>
{:else if setup == "riddle"}
	<div class="flex flex-wrap">
		<div class="grid grow">
			<label for="riddle_gid">GID:</label>
			<input
				type="text"
				name="riddle_gid"
				maxlength="10"
				id="riddle_gid"
				bind:value={riddleSet.gid}
			/>
		</div>
		<div class="grid grow">
			<label for="riddle_rid">RID:</label>
			<input
				type="text"
				name="riddle_rid"
				id="riddle_rid"
				bind:value={riddleSet.rid}
			/>
		</div>

		<div class="grid grow">
			<label for="rd_address">Next Riddle Address:</label>
			<input
				type="text"
				name="rd_address"
				id="rd_address"
				bind:value={riddleSet.rd_content.address}
			/>
		</div>
	</div>

	<div class="flex flex-wrap">
		<div class="grid grow">
			<label for="riddle_header">Header:</label>
			<input
				type="text"
				name="riddle_header"
				id="riddle_header"
				bind:value={riddleSet.header}
			/>
		</div>
		<div class="grid grow">
			<label for="riddle_aside">Aside:</label>
			<input
				type="text"
				name="riddle_aside"
				id="riddle_aside"
				bind:value={riddleSet.aside}
			/>
		</div>
		<div class="grid grow">
			<label for="riddle_footer">Footer:</label>
			<input
				type="text"
				name="riddle_footer"
				id="riddle_footer"
				bind:value={riddleSet.footer}
			/>
		</div>
	</div>

	<div class="grid">
		<label for="riddle_main">Main:</label>
		<input
			type="text"
			name="riddle_main"
			id="riddle_main"
			bind:value={riddleSet.main}
		/>
	</div>

	<div class="grid">
		<label for="riddle_customStyling">Custom styling:</label>
		<input
			type="text"
			name="riddle_customStyling"
			id="riddle_customStyling"
			bind:value={riddleSet.customStyling}
		/>
	</div>

	<div class="grid p-4 my-4 border-2 border-dashed border-gray-400 ">
		<div class="flex place-content-between items-center mb-4">
			<div class="flex items-center gap-2">
				<input
					type="checkbox"
					name="rd_hasMap"
					id="rd_hasMap"
					bind:checked={hasMap}
				/>
				<label for="rd_hasMap" class="m-0 font-bold uppercase text-lg">Enable Map for this Riddle</label>
			</div>
			{#if hasMap}
				<button class="secondary text-sm m-0" on:click={refreshMapPreview}>
					<i class="fa-solid fa-arrows-rotate"></i> Refresh Preview
				</button>
			{/if}
		</div>

		{#if hasMap}
			<div class="flex flex-wrap gap-4 mb-2">
				<div class="grid grow">
					<label for="cen-lng" class="text-xs">Center Lng:</label>
					<input id="cen-lng"type="number" step="0.00001" bind:value={map_content.center[0]} />
				</div>
				<div class="grid grow">
					<label for="cen-lat"class="text-xs">Center Lat:</label>
					<input id="cen-lat"type="number" step="0.00001" bind:value={map_content.center[1]} />
				</div>
				<div class="grid grow">
					<label for="cen-zoom"class="text-xs">Initial Zoom:</label>
					<input id="cen-zoom"type="number" step="0.1" bind:value={map_content.zoom} />
				</div>
			</div>

			<div class="flex flex-wrap gap-4 mb-4">
				<div class="grid grow">
					<label for="bounds-sw-lng" class="text-xs">Bounds SW Lng:</label>
					<input id="bounds-sw-lng" type="number" step="0.00001" bind:value={map_content.bounds.sw[0]} />
				</div>
				<div class="grid grow">
					<label for="bounds-sw-lat" class="text-xs">Bounds SW Lat:</label>
					<input id="bounds-sw-lng"type="number" step="0.00001" bind:value={map_content.bounds.sw[1]} />
				</div>
				<div class="grid grow">
					<label for="bounds-ne-lng" class="text-xs">Bounds NE Lng:</label>
					<input id="bounds-ne-lng" type="number" step="0.00001" bind:value={map_content.bounds.ne[0]} />
				</div>
				<div class="grid grow">
					<label for="bounds-ne-lat" class="text-xs">Bounds NE Lat:</label>
					<input id="bounds-ne-lat"type="number" step="0.00001" bind:value={map_content.bounds.ne[1]} />
				</div>
			</div>

			<div class="flex flex-wrap gap-4 mb-4">
				<div class="flex items-center gap-2 grow">
					<input type="checkbox" id="map_geo" bind:checked={map_content.geolocate} />
					<label for="map_geo" class="m-0">Enable Geolocation</label>
				</div>
				<div class="flex items-center gap-2 grow">
					<input type="checkbox" id="map_nav" bind:checked={map_content.navigation} />
					<label for="map_nav" class="m-0">Enable Navigation UI</label>
				</div>
			</div>

			<div class="grid mb-4">
				<label for="mapbox-style-url" class="text-xs">Mapbox Style URL:</label>
				<input id="mapbox-style-url"  type="text" bind:value={map_content.style} />
			</div>

			<div class="my-4 border-t border-gray-300 pt-4">
				<h3 class="font-bold mb-2">Map Markers</h3>
				{#each map_content.markers as marker, i}
					<div class="flex gap-4 mb-2 items-end">
						<div class="grid grow">
							<label for="{i}-lng" class="text-xs">Lng:</label>
							<input id="{i}-lng" type="number" step="0.00001" bind:value={marker.lnglat[0]} />
						</div>
						<div class="grid grow">
							<label for="{i}-lat" class="text-xs">Lat:</label>
							<input id="{i}-lat" type="number" step="0.00001" bind:value={marker.lnglat[1]} />
						</div>
						<button class="secondary m-0" on:click={() => map_content.markers = map_content.markers.filter((_, idx) => idx !== i)}>
							<i class="fa-solid fa-trash"></i>
						</button>
					</div>
				{/each}
				<button class="text-sm mt-2 outline w-max" on:click={() => map_content.markers = [...(map_content.markers || []), { lnglat: [map_content.center[0], map_content.center[1]], options: {} }]}>
					<i class="fa-solid fa-plus"></i> Add Marker
				</button>
				
				<div class="grid mt-4">
					<label for="marker-custom-css" class="text-xs">Marker Custom CSS (Optional):</label>
					<textarea id="marker-custom-css" class="font-mono text-xs w-full text-black" rows="3" bind:value={map_content.markerStyling} placeholder=""></textarea>
                    <small>{"<style> .oblsk-marker { background: red; } </style>"}</small>
				</div>
			</div>

			<div class="my-4 border-t border-gray-300 pt-4">
				<h3 class="font-bold mb-2">Zones / Geofences (Polygons)</h3>
				{#each map_content.polygons as poly, i}
					<div class="grid gap-2 mb-4 p-4 border border-gray-300 relative ">
						<button class="absolute top-2 right-2 text-red-600 hover:text-red-800 m-0 bg-transparent border-0" on:click={() => {
							map_content.polygons = map_content.polygons.filter((_, idx) => idx !== i);
							polyCoordsText = polyCoordsText.filter((_, idx) => idx !== i);
						}}><i class="fa-solid fa-trash text-lg"></i></button>
						
						<p class="font-bold text-sm m-0">Zone ID: {poly.id || 'N/A'}</p>
						
						<div class="flex flex-wrap gap-4 mt-2">
							<div class="grid grow">
								<label for="poly-fill" class="text-xs">Fill Color & Opacity:</label>
								<div class="flex items-center gap-2">
									<input id="poly-fill" type="color" class="w-12 h-10 p-1 border-0" bind:value={poly.fillColor} />
									<input id="poly-opacity" type="number" step="0.1" min="0" max="1" bind:value={poly.fillOpacity} class="w-20" placeholder="0-1" title="Opacity" />
								</div>
							</div>
							<div class="grid grow">
								<label for="poly-border" class="text-xs">Border Color & Width:</label>
								<div class="flex items-center gap-2">
									<input id="poly-border" type="color" class="w-12 h-10 p-1 border-0" bind:value={poly.borderColor} />
									<input id="poly-border-opacity"type="number" step="1" min="0" bind:value={poly.borderWidth} class="w-20" placeholder="Width" title="Border Width" />
								</div>
							</div>
						</div>
						
						<div class="flex gap-4 my-2">
							<div class="flex items-center gap-2 grow">
								<input type="checkbox" id={"poly_play_" + i} bind:checked={poly.isPlayArea} />
								<label for={"poly_play_" + i} class="m-0 text-sm">Is Play Area</label>
							</div>
							<div class="flex items-center gap-2 grow">
								<input type="checkbox" id={"poly_inv_" + i} bind:checked={poly.invisible} />
								<label for={"poly_inv_" + i} class="m-0 text-sm">Invisible Boundary</label>
							</div>
						</div>
						
						<div class="grid mt-2">
							<label for="poly-coords" class="text-xs">Coordinates (Paste Lng, Lat per line):</label>
							<textarea id="poly-coords" class="font-mono text-xs w-full text-black" rows="5" bind:value={polyCoordsText[i]} placeholder="24.735, 42.150&#10;24.739, 42.150&#10;..."></textarea>
						</div>
					</div>
				{/each}
				<button class="text-sm mt-2 outline w-max" on:click={() => {
					map_content.polygons = [...(map_content.polygons || []), { id: "zone-" + Date.now(), fillColor: "#FF0000", fillOpacity: 0.3, borderColor: "#8B0000", borderWidth: 2, isPlayArea: false, invisible: false, coordinates: [] }];
					polyCoordsText = [...polyCoordsText, ""];
				}}>
					<i class="fa-solid fa-plus"></i> Add Zone
				</button>
			</div>

			<div class="w-full h-[400px] relative mt-4 border-2 border-gray-800">
				{#key mapPreviewKey}
					<Mapbox {lang} mapData={map_content} />
				{/key}
			</div>
			<p class="text-xs text-gray-500 mt-2">* Click 'Refresh Preview' after changing parameters to see updates on the map above.</p>
		{/if}
	</div>

	<div class="grid p-4 my-4 border-2 border-dashed border-gray-400 ">
		<div class="flex place-content-between items-center mb-4">
			<div class="flex items-center gap-2">
				<input type="checkbox" id="rd_hasGeo" bind:checked={hasGeoContent} />
				<label for="rd_hasGeo" class="m-0 font-bold uppercase text-lg">Enable Geo-Locked Riddle</label>
			</div>
		</div>

		{#if hasGeoContent}
			<div class="flex flex-wrap gap-4 mb-2">
				<div class="grid grow">
					<label for="geolock-lat" class="text-xs">Latitude (N):</label>
					<input id="geolock-lat" type="number" step="0.00001" bind:value={geo_content.coords[0]} />
				</div>
				<div class="grid grow">
					<label for="geolock-lng" class="text-xs">Longitude (E):</label>
					<input id="geolock-lng" type="number" step="0.00001" bind:value={geo_content.coords[1]} />
				</div>
				<div class="grid grow">
					<label for="geolock-range" class="text-xs">Range (meters):</label>
					<input id="geolock-range" type="number" step="1" bind:value={geo_content.range} />
				</div>
			</div>

			<div class="flex flex-wrap gap-4 mb-4">
				<div class="flex items-center gap-2 grow">
					<input type="checkbox" id="geo_lock" bind:checked={geo_content.geolocked} />
					<label for="geo_lock" class="m-0 text-sm">Is Geolocked?</label>
				</div>
				<div class="flex items-center gap-2 grow">
					<input type="checkbox" id="geo_details" bind:checked={geo_content.showGeoDetails} />
					<label for="geo_details" class="m-0 text-sm">Show Geo Details</label>
				</div>
				<div class="flex items-center gap-2 grow">
					<input type="checkbox" id="geo_accuracy" bind:checked={geo_content.options.enableHighAccuracy} />
					<label for="geo_accuracy" class="m-0 text-sm">High Accuracy</label>
				</div>
			</div>

			<div class="flex flex-wrap gap-4 mb-4">
				<div class="grid grow">
					<label for="geolock-timout" class="text-xs">Timeout (ms):</label>
					<input id="geolock-timout" type="number" step="1" bind:value={geo_content.options.timeout} />
				</div>
				<div class="grid grow">
					<label for="geolock-maxage" class="text-xs">Maximum Age (ms):</label>
					<input id="geolock-maxage" type="number" step="1" bind:value={geo_content.options.maximumAge} />
				</div>
			</div>

			<div class="grid mt-2">
				<label for="geolock-main"class="text-xs">Geo Main (HTML / Image):</label>
				<textarea id="geolock-main" class="font-mono text-xs w-full text-black" rows="3" bind:value={geo_content.geo_main} placeholder='<img src="..." />'></textarea>
			</div>
		{/if}
	</div>
	<div class="flex flex-wrap">
		<div class="grid grow">
			<p>Riddle type:</p>
			<select bind:value={riddleSet.rd_content.type}>
				{#each types.names as type, i}
					<option value={types.values[i]}>{type}</option>
				{/each}
			</select>
		</div>

		{#if riddleSet.rd_content.type == "dnd-reorder" || riddleSet.rd_content.type == "dnd-reorder-show-input"}
			<div class="grid grow">
				<p>Order type:</p>
				<select bind:value={riddleSet.rd_content.order_type}>
					<option selected disabled value="disabled">Order type</option>
					<option value="horizontal">Horizontal</option>
					<option value="vertical">Vertical</option>
					<option value="grid">Grid</option>
				</select>
			</div>
		{:else if riddleSet.rd_content.type == "btn-order" || riddleSet.rd_content.type == "btn-order-hidden" || riddleSet.rd_content.type == "btn-to-input"}
			<div class="grid grow">
				<p>Order type:</p>
				<select bind:value={riddleSet.rd_content.panel_type}>
					<option selected disabled value="disabled">Panel type</option>
					<option value="horizontal">Horizontal</option>
					<option value="vertical">Vertical</option>
					<option value="grid">Grid</option>
				</select>
			</div>
		{/if}
	</div>

	{#if riddleSet.rd_content.type == "dnd-reorder" || riddleSet.rd_content.type == "dnd-reorder-show-input"}
		{#if riddleSet.rd_content.order_type == "grid"}
			<div class="flex flex-wrap">
				<div class="grid grow">
					<label for="order-rows">ROWS:</label>
					<input
						type="number"
						name="order-rows"
						id="order-rows"
						min="1"
						bind:value={riddleSet.rd_content.order_size.rows}
					/>
				</div>
				<div class="grid grow">
					<label for="order-cols">COLS:</label>
					<input
						type="number"
						name="order-cols"
						id="order-cols"
						min="1"
						bind:value={riddleSet.rd_content.order_size.cols}
					/>
				</div>
			</div>
		{/if}

		<div class="grid">
			{#each riddleSet.rd_content.order_id_list as order_id, i}
				<div class="flex flex-wrap">
					<div class="grid w-min grow">
						<label for={"rd_order_id_" + i}>ID {i + 1}:</label>
						<input
							type="text"
							name={"rd_order_id_" + i}
							id={"rd_order_id_" + i}
							bind:value={riddleSet.rd_content.order_id_list[i]}
						/>
					</div>

					<div class="grid grow">
						<label for={"rd_order_" + i}>Content {i + 1}:</label>
						<div class="flex">
							<input
								type="text"
								name={"rd_order_" + i}
								id={"rd_order_" + i}
								bind:value={riddleSet.rd_content.order_list[i]}
							/>
							<button
								on:click={() => {
									riddleSet.rd_content.order_id_list = riddleSet.rd_content.order_id_list.filter((_, index) => index !== i);
									riddleSet.rd_content.order_list = riddleSet.rd_content.order_list.filter((_, index) => index !== i);
								}}
								class="secondary"><i class="fa-solid fa-minus" /></button
							>
						</div>
					</div>
				</div>
			{/each}
		</div>
		<button
			on:click={() => (
				riddleSet.rd_content.order_list.length++,
				riddleSet.rd_content.order_id_list.length++
			)}><i class="fa-solid fa-plus" data-position="left" />New item</button
		>
	{/if}
	
	{#if riddleSet.rd_content.type == "btn-order" || riddleSet.rd_content.type == "btn-order-hidden" || riddleSet.rd_content.type == "btn-to-input"}
		{#if riddleSet.rd_content.panel_type == "grid"}
			<div class="flex flex-wrap">
				<div class="grid grow">
					<label for="panel-rows">ROWS:</label>
					<input
						type="number"
						name="panel-rows"
						id="panel-rows"
						min="1"
						bind:value={riddleSet.rd_content.panel_size.rows}
					/>
				</div>
				<div class="grid grow">
					<label for="panel-cols">COLS:</label>
					<input
						type="number"
						name="panel-cols"
						id="panel-cols"
						min="1"
						bind:value={riddleSet.rd_content.panel_size.cols}
					/>
				</div>
			</div>
		{/if}

		<div class="grid">
			{#each riddleSet.rd_content.panel_id_list as panel_id, i}
				<div class="flex flex-wrap">
					<div class="grid w-min grow">
						<label for={"rd_panel_id_" + i}>ID {i + 1}:</label>
						<input
							type="text"
							name={"rd_panel_id_" + i}
							id={"rd_panel_id_" + i}
							bind:value={riddleSet.rd_content.panel_id_list[i]}
						/>
					</div>

					<div class="grid grow">
						<label for={"rd_panel_" + i}>Content {i + 1}:</label>
						<div class="flex">
							<input
								type="text"
								name={"rd_panel_" + i}
								id={"rd_panel_" + i}
								bind:value={riddleSet.rd_content.panel_list[i]}
								class=" grow"
							/>
							<button
								on:click={() => {
									riddleSet.rd_content.panel_id_list = riddleSet.rd_content.panel_id_list.filter((_, index) => index !== i);
									riddleSet.rd_content.panel_list = riddleSet.rd_content.panel_list.filter((_, index) => index !== i);
								}}
								class="secondary"><i class="fa-solid fa-minus" /></button
							>
						</div>
					</div>
				</div>
			{/each}
			<p><small>REMINDER: Add thumbnails in the console.</small></p>
		</div>
		<button
			on:click={() => (
				riddleSet.rd_content.panel_list.length++,
				riddleSet.rd_content.panel_id_list.length++
			)}><i class="fa-solid fa-plus" data-position="left" />New item</button
		>
	{/if}

	<div class="grid">
		<p>Answers</p>

		{#if riddleSet.rd_content.type == "mc-multiple" || riddleSet.rd_content.type == "qr-mc-multiple"}
			<p><small>&#123;"key":"value"&#125;</small></p>
			{#each complexAnswerTemp as answer, i}
				<div class="flex">
					<input
						type="text"
						name={"rd_answers_" + i}
						id={"rd_answers_" + i}
						bind:value={complexAnswerTemp[i]}
						class=" grow"
						placeholder={"- answer " + (i + 1) + " -"}
						on:change={() => {
							if (complexAnswerTemp[i].includes("{") && complexAnswerTemp[i].includes("}")) {
								riddleSet.rd_content.answers[i] = JSON.parse(complexAnswerTemp[i]);
							} else {
								riddleSet.rd_content.answers[i] = complexAnswerTemp[i];
							}
						}}
					/>
					<button
						on:click={() => complexAnswerTemp = complexAnswerTemp.filter((_, index) => index !== i)}
						class="secondary"><i class="fa-solid fa-minus" /></button
					>
				</div>
			{/each}

			<button on:click={() => complexAnswerTemp.length++}>
				<i class="fa-solid fa-plus" data-position="left" />New answer</button
			>
		{:else}
			{#each riddleSet.rd_content.answers as answer, i}
				<div class="flex">
					<input
						type="text"
						name={"rd_answers_" + i}
						id={"rd_answers_" + i}
						bind:value={riddleSet.rd_content.answers[i]}
						class=" grow"
						placeholder={"- answer " + (i + 1) + " -"}
					/>
					<button
						on:click={() => riddleSet.rd_content.answers = riddleSet.rd_content.answers.filter((_, index) => index !== i)}
						class="secondary"><i class="fa-solid fa-minus" /></button
					>
				</div>
			{/each}
			<button on:click={() => riddleSet.rd_content.answers.length++}>
				<i class="fa-solid fa-plus" data-position="left" />New answer</button
			>
		{/if}
	</div>

	<div class="grid">
		<p>Hints</p>
		{#if riddleSet.rd_content.hints}
			{#each riddleSet.rd_content.hints as hint, i}
				<div class="flex flex-wrap">
					<input
						type="text"
						name={"rd_hints_guide_" + i}
						id={"rd_hints_guide_" + i}
						placeholder="Guide"
						title="Guide"
						bind:value={riddleSet.rd_content.hints[i].guide}
						class=" grow"
					/>
					<input
						type="text"
						name={"rd_hints_content_" + i}
						id={"rd_hints_content_" + i}
						placeholder="Content"
						title="Content"
						bind:value={riddleSet.rd_content.hints[i].content}
						class=" grow"
					/>
					<input
						type="number"
						min="0"
						name={"rd_hints_time_" + i}
						id={"rd_hints_time_" + i}
						placeholder="Penalty"
						title="Penalty"
						bind:value={riddleSet.rd_content.hints[i].time}
						class=" grow"
					/>
					<button
						on:click={() => riddleSet.rd_content.hints = riddleSet.rd_content.hints.filter((_, index) => index !== i)}
						class="secondary"><i class="fa-solid fa-minus" /></button>
				</div>
			{/each}
		{/if}
		<button
			on:click={() => (
				riddleSet.rd_content.hints = [...riddleSet.rd_content.hints, { guide: "", content: "", time: 0 }]
			)}><i class="fa-solid fa-plus" data-position="left" />New hint</button>
	</div>
	<div class="flex">
		<input
			type="checkbox"
			name="rd_isBonus"
			id="rd_isBonus"
			bind:checked={riddleSet.rd_content.isBonus}
		/><label for="rd_isBonus">This is a skippable bonus riddle?</label>
	</div>

    <div class="grid p-4 my-4 border-2 border-dashed border-gray-400 ">
		<div class="flex items-center gap-2 mb-2">
			<input type="checkbox" id="game_show_timer" bind:checked={riddleSet.showTimer} />
			<label for="game_show_timer" class="m-0 font-bold uppercase text-lg">Show Game Timer</label>
		</div>

		{#if riddleSet.showTimer}
			<div class="grid mt-2">
				<label for="timer-css" class="text-xs">Timer Custom CSS (Optional):</label>
				<textarea id="timer-css" class="font-mono text-xs w-full text-black" rows="3" bind:value={riddleSet.timerStyling} placeholder=""></textarea>
                <small>{"<style> .timer-container { background-color: #05204a; } .timer-container .clock-title { display: none; } </style>"}"</small>
			</div>
		{/if}
	</div>

	<div class="grid flex-wrap sm:flex">
		<button on:click={() => (setup = "base")} class="outline grow">
			<i class="fa-solid fa-circle-left" data-position="left" />Back</button
		>

		<button
			on:click={saveRiddleSet}
			class="order-first secondary grow sm:order-none"
		>
			<i class="fa-solid fa-floppy-disk" data-position="left" />Save Riddle Set</button
		>
	</div>

{:else if setup == "open-game"}
	<label for="open-gid">GID:</label>
	<input
		type="text"
		name="open-gid"
		maxlength="10"
		id="open-gid"
		bind:value={openSet.gid}
		placeholder="Open Game ID"
		title="Game ID of the game you want to open"
	/>
	<button on:click={handleOpenGame}
		><i class="fa-solid fa-folder-open" data-position="left" />Open Game</button
	>
	<button on:click={() => (setup = "base")}
		><i class="fa-solid fa-circle-left" data-position="left" />Back</button
	>
{:else if setup == "open-riddle"}
	<label for="open-gid">GID:</label>
	<input
		type="text"
		name="open-gid"
		maxlength="10"
		id="open-gid"
		bind:value={openSet.gid}
		placeholder="Open Game ID"
	/>
	<label for="open-gid">RID:</label>
	<input
		type="text"
		name="open-rid"
		id="open-rid"
		bind:value={openSet.rid}
		placeholder="Open Riddle ID"
	/>
	<button on:click={handleOpenRiddle}
		><i class="fa-solid fa-folder-open" data-position="left" />Open Riddle</button
	>
	<button on:click={() => (setup = "base")}
		><i class="fa-solid fa-circle-left" data-position="left" />Back</button
	>
{/if}

<div class="grid gap-4" />

<style>
	:global(.obelisk select) {
		box-sizing: border-box;
		place-content: center;
		--tw-bg-opacity: 1;
		background-color: rgb(255 255 255 / var(--tw-bg-opacity));
	}
	:global(.obelisk select) {
		margin: 10px;
		padding: 15px;
		border-radius: 0px;
		box-shadow: #093a86 5px 5px 0;
		color: #05204a;
	}
</style>